/* lineCategories.js — Phase 3a BS/CFS chart-of-accounts taxonomy.

   Mirrors lineRoles.js (Phase 2a IS-side) for the BS/CFS side. Provides:

   - BS_CATEGORIES: top-level BS sections (current_assets, equity, etc.)
   - BS_ROLES: per-leaf role tags. Roles drive (a) CFS section assignment,
     (b) BS engine special handling (retained_earnings formula reset, etc.),
     (c) future per-role assumption defaults (Phase 6).
   - CFS_EXCLUDED_ROLES: roles that DO NOT generate CFS activity entries
     (cash sits inside the cash reconciliation, RE/NI flow through net_income,
     accumulated D&A is captured by the D&A add-back line).
   - SYNTHETIC_BS_LINES: headers / totals / check rows that are always
     present regardless of the company chart of accounts, plus moduleGated
     deferred-revenue leaves that exist only when the corresponding revenue
     module is wired (mirrors Phase 2a's rev_ae_quota / rev_company_quota
     IS-side gating).
   - SYNTHETIC_CFS_LINES: CFS skeleton (oper/wc/invest/fin headers, totals,
     formulas, ppp_loan / loan_writeoff oneoffs, mm_sweep, beg_cash/end_cash).

   No engine consumes this module yet — Phase 3a only validates that the
   builder output reproduces the hardcoded BS_LINES / CFS_LINES exactly.
*/

export const BS_CATEGORIES = {
  CURRENT_ASSETS: 'current_assets',
  NON_CURRENT_ASSETS: 'non_current_assets',
  CURRENT_LIABILITIES: 'current_liabilities',
  NON_CURRENT_LIABILITIES: 'non_current_liabilities',
  EQUITY: 'equity',
};

export const BS_ROLES = {
  // Current assets
  CASH: 'cash',
  CLEARING: 'clearing',
  SWEEP_TARGET: 'sweep_target',
  ACCOUNTS_RECEIVABLE: 'accounts_receivable',
  PREPAID: 'prepaid',
  // Non-current assets
  FIXED_ASSET: 'fixed_asset',
  INTANGIBLE_ASSET: 'intangible_asset',
  ACCUMULATED_DEPRECIATION: 'accumulated_depreciation',
  ACCUMULATED_AMORTIZATION: 'accumulated_amortization',
  // Liabilities
  ACCOUNTS_PAYABLE: 'accounts_payable',
  ACCRUED: 'accrued',
  DEFERRED_REVENUE: 'deferred_revenue',
  DEBT: 'debt',
  // A loan or line of credit due within a year: a CURRENT liability on the balance sheet,
  // held constant like long-term debt, with its changes in FINANCING on the cash flow
  // (buildCFSLines.classifyForCFS). Chosen or suggested at Step 4a.
  SHORT_TERM_DEBT: 'short_term_debt',
  // Equity
  CONTRIBUTED_CAPITAL: 'contributed_capital',
  // Phase 3b: split into two distinct roles. The BS engine looks these up
  // by role to find the correct line.id (replacing hardcoded "retained_earnings"
  // / "net_income" lookups). Old single-role names retained as aliases for
  // any back-compat reads.
  ROLLING_RETAINED_EARNINGS: 'rolling_retained_earnings', // RE roll-forward (Jan reset to prior Dec RE+NI)
  YTD_NET_INCOME:            'ytd_net_income',            // YTD net income (resets each January)
  RETAINED_EARNINGS:         'retained_earnings',         // alias — pre-Phase-3b
  NET_INCOME:                'net_income',                // alias — pre-Phase-3b
};

// Roles that are NOT translated into chg_/inv_/fin_ CFS activity lines.
// - cash/clearing/sweep_target ARE the cash reconciliation, not flows
// - accumulated_depreciation/amortization are captured by the D&A add-back
// - rolling_retained_earnings + ytd_net_income flow through the net_income IS line
//   (old aliases retained_earnings / net_income kept for any pre-Phase-3b reads)
export const CFS_EXCLUDED_ROLES = new Set([
  'cash',
  'clearing',
  'sweep_target',
  'accumulated_depreciation',
  'accumulated_amortization',
  'rolling_retained_earnings',
  'ytd_net_income',
  'retained_earnings', // alias
  'net_income',        // alias
]);

export const SYNTHETIC_BS_LINES = {
  // Headers, subtotals, and the BS check row — emitted regardless of meta.
  alwaysPresent: {
    assets_header:            { label: 'ASSETS',                        level: 0, type: 'header' },
    ca_header:                { label: 'Current Assets',                level: 1, type: 'header' },
    clearing_header:          { label: 'Clearing',                      level: 2, type: 'header' },
    fa_header:                { label: 'Fixed Assets',                  level: 1, type: 'header' },
    ia_header:                { label: 'Intangible Assets',             level: 1, type: 'header' },
    liabilities_header:       { label: 'LIABILITIES',                   level: 0, type: 'header' },
    cl_header:                { label: 'Current Liabilities',           level: 1, type: 'header' },
    lt_header:                { label: 'Long Term Liabilities',         level: 1, type: 'header' },
    equity_header:            { label: 'EQUITY',                        level: 0, type: 'header' },
    total_clearing:           { label: 'Total Clearing',                level: 2, type: 'total' },
    total_current_assets:     { label: 'Total Current Assets',          level: 1, type: 'total' },
    total_fixed_assets:       { label: 'Total Fixed Assets',            level: 1, type: 'total' },
    total_intangible_assets:  { label: 'Total Intangible Assets',       level: 1, type: 'total' },
    total_assets:             { label: 'TOTAL ASSETS',                  level: 0, type: 'total' },
    total_current_liabilities:{ label: 'Total Current Liabilities',     level: 1, type: 'total' },
    total_lt_liabilities:     { label: 'Total Long Term Liabilities',   level: 1, type: 'total' },
    total_liabilities:        { label: 'TOTAL LIABILITIES',             level: 0, type: 'total' },
    total_equity:             { label: 'TOTAL EQUITY',                  level: 0, type: 'total' },
    total_le:                 { label: 'TOTAL LIABILITIES & EQUITY',    level: 0, type: 'total' },
    check:                    { label: 'Balance Check',                 level: 0, type: 'check'  },   // W-017: the BS page's last row (label 'Balance Check' since 2026-10-30 -- Brian 10-29; was 'Out of balance')
  },

  // Module-gated leaves: were previously hardcoded synthetic entries for
  // deferred_rev_ae_quota / deferred_rev_company_quota. Session 0 (2026-04-27)
  // moved this wiring to be data-driven from revenue_module_mappings.bs_line_key
  // inside buildBSLinesForCompany. The builder now:
  //   - emits a synthetic moduleGated DR line for each revMappings row whose
  //     bs_line_key is NOT in bs_actuals_meta (the Semitek/Chris Test case),
  //   - or augments the matching historical bs_actuals_meta entry with
  //     module_link_ref derived from module_type (the AE Model case).
  // This array is retained empty for future non-revenue moduleGated entries
  // (currently none on the BS side).
  moduleGated: [],
};

// Synthetic CFS skeleton — emitted by buildCFSLinesForCompany regardless of
// meta. Activity lines (chg_*, inv_*, fin_*) are derived from BS leaves.
//
// Sweep/cash-account labels are PER-TENANT and are templated onto per-tenant
// COPIES inside buildCFSLinesForCompany (never mutated here) from
// bs_actuals_meta: sweepLabel = meta[role='sweep_target'].label; clearingLabel =
// meta['total_clearing'].display_label ?? 'Total Clearing'. The neutral defaults
// below are placeholders only — buildCFSLinesForCompany always overrides
// cash_avail / mm_sweep / beg_cash / end_cash. (They previously hardcoded
// Semitek's "Chase Money Market", which leaked that bank name onto every tenant's
// CFS.)
//
// One-offs (ppp_loan / loan_writeoff / …) are NOT part of this skeleton: they are
// data-gated on the tenant's own cfs_oneoffs blob and built in buildCFSLines.js.
// fa_writeoff STAYS here — it is DERIVED from fixed-asset gross drops, not gated
// on cfs_oneoffs (which only supplies its per-month override).
export const SYNTHETIC_CFS_LINES = {
  oper_header:    { label: 'Cash From Operating Activities:',                 level: 0, type: 'header' },
  net_income:     { label: 'Net Income',                                      level: 1, source: 'is', isKey: 'net_income_monthly' },
  da_addback:     { label: 'Plus: Depreciation & Amortization',               level: 1, source: 'is', isKey: 'da_monthly' },
  fa_writeoff:    { label: 'Write off of Fixed Assets',                       level: 1, source: 'fa_writeoff' },
  wc_header:      { label: 'Changes in Working Capital:',                     level: 1, type: 'header' },
  total_chg_wc:   { label: 'Total Change in Working Capital',                 level: 1, type: 'total' },
  total_oper:     { label: 'Total Cash From Operating Activities',            level: 0, type: 'total' },
  s1:             { label: '',                                                level: 0, type: 'spacer' },
  invest_header:  { label: 'Cash From Investing Activities:',                 level: 0, type: 'header' },
  total_invest:   { label: 'Total Cash From Investing Activities',            level: 0, type: 'total' },
  s2:             { label: '',                                                level: 0, type: 'spacer' },
  fin_header:     { label: 'Cash From Financing Activities:',                 level: 0, type: 'header' },
  // cash_avail / mm_sweep / beg_cash / end_cash: labels below are neutral
  // placeholders — buildCFSLinesForCompany templates the tenant's real
  // sweep + clearing account labels onto per-tenant copies.
  cash_avail:     { label: 'Cash Available/(Required) Before Sweep',          level: 1, type: 'formula' },
  s_sw:           { label: '',                                                level: 1, type: 'spacer' },
  mm_sweep:       { label: 'Sweep (Replenishment) / Drawdown',                level: 1, source: 'sweep' },
  total_fin:      { label: 'Total Cash From Financing Activities',            level: 0, type: 'total' },
  s3:             { label: '',                                                level: 0, type: 'spacer' },
  beg_cash:       { label: 'Beginning Cash',                                  level: 0, type: 'formula' },
  chg_cash:       { label: 'Change in Cash',                                  level: 1, type: 'formula' },
  end_cash:       { label: 'Ending Cash',                                     level: 0, type: 'formula', bold: true },
};
